import _ from 'lodash';
import angular from 'angular';
import OT from '@opentok/client';
import to from 'await-to-js';
import { ConnectionStateCameraStatus } from './../common/interfaces/person-connection';
import AbstractVideoProvider, { AudioTypes, MediaModes, } from '../abstract-video-provider';
import TokboxPersonConnection from './lib/tokbox-person-connection.class';
import TokboxSelfConnection from './lib/tokbox-self-connection.class';
import QueryString from './lib/query-string';
import { TokboxVolumeListener } from './tokbox-volume-listener';
import { TokboxMicrophoneRenderer } from './tokbox-microphone-renderer';
import TokboxPublisherListener from './lib/tokbox-publisher-listener';
import * as devices from '../../../modules/devices';
import * as call from '../../../modules/call';
import { setCaptions, updateCaptionsArray } from '../../../modules/captions';
import { getCaptions, getIsCaptionsOpen, } from '../../../modules/captions/selectors';
import * as localMedia from '../../../modules/local-media';
import { OtPublisherErrorHandler } from './lib/ot-publisher-error-handler';
import { BASE64_PREFIX } from '../../../modules/core/constants';
import { RootScopeEvents } from '../../../common/services/root-scope-types';
import { TokboxSignalService } from './lib/tokbox-signal-service.class';
import { isDoctor } from '../../../modules/call/helpers';
import { HTMLMediaElementEvents } from '../../../modules/call/constants/html-video-element';
import { OT_ERRORS } from './lib/errors-types';
import { initBackgroundEffect } from '../../../modules/call/actions';
import { VideoQuality } from '../../../modules/call/constants';
import { getBackgroundEffect } from '../../../modules/call/selectors';
import { createDeferred } from '../../../modules/core/helpers';
import { localeService } from '../../../modules/core/helpers/locale-service';
import { VideoProviderEvents } from '../';
import { store } from '../../../store/store';
const { DeviceFacingMode } = devices.constants;
// OT.setLogLevel(OT['DEBUG']);
const MAX_TIME_TO_RECONNECT = 30000;
let androidChrome86FixApplied = false;
export var OTPublisherErrors;
(function (OTPublisherErrors) {
    OTPublisherErrors["NotAllowedError"] = "NotAllowedError";
})(OTPublisherErrors || (OTPublisherErrors = {}));
export var OTPublisherEvents;
(function (OTPublisherEvents) {
    OTPublisherEvents["Destroyed"] = "destroyed";
})(OTPublisherEvents || (OTPublisherEvents = {}));
const AUDIO_TYPE_CONFIGURATION_MAP = {
    [AudioTypes.MEDICAL]: {
        audioBitrate: 510000,
        disableAudioProcessing: true,
        noiseSuppression: false,
        echoCancellation: false,
    },
    [AudioTypes.STANDARD]: {},
};
/* @ngInject */
export default class TokboxVideoProvider extends AbstractVideoProvider {
    constructor($injector, OTSession, LoopBackAuth, Room, Browser, stats, globalUi, screenshareFactory, selfMicrophoneRenderer) {
        super($injector);
        this.$injector = $injector;
        this.OTSession = OTSession;
        this.LoopBackAuth = LoopBackAuth;
        this.Room = Room;
        this.Browser = Browser;
        this.stats = stats;
        this.globalUi = globalUi;
        this.screenshareFactory = screenshareFactory;
        this.selfMicrophoneRenderer = selfMicrophoneRenderer;
        this.name = 'tokbox';
        this.connections = [];
        this.priorityVideoCodec = null;
        this.applyPriorityCodec = true;
        this.isReconnecting = false;
        this.selfCaptionsSubscriber = null; // Hidden subscriber for self-captions
        this.otPublisherErrorHandler = new OtPublisherErrorHandler($injector);
        this.tokboxSignalService = new TokboxSignalService(this, OTSession, this.$log);
        if (this.Browser.isIosSupportedH264()) {
            this.priorityVideoCodec = 'h264';
        }
        this._registerOTEvents();
        this.$rootScope.$on(RootScopeEvents.USER_ACTIONS.FLIP_CAMERA, () => {
            this.OTSession.publisher.cycleVideo().then(({ deviceId }) => {
                this.updateCameraSettings(deviceId);
            });
        });
        this.$rootScope.$on(RootScopeEvents.STREAM.VIDEO_ELEMENT_CREATED, () => {
            // TODO: FIXME in subscriber on videoElementCreated run update streams
            this.updateStreams();
        });
        // this.addListenerIfNotExists('_streamCreated', this._onStreamCreated.bind(this));
        this.qualityMediaConfig = {
            [VideoQuality.Low]: {
                width: { ideal: 320 },
                height: { ideal: 240 },
                frameRate: { ideal: 20 },
            },
            [VideoQuality.Medium]: {
                width: { ideal: 640 },
                height: { ideal: 480 },
                frameRate: { ideal: 30 },
            },
            [VideoQuality.High]: {
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 40 },
            },
        };
        this.qualityConfig = {
            [VideoQuality.Low]: {
                frameRate: 15,
                audioBitrate: 40000,
                resolution: '320x240',
            },
            [VideoQuality.Medium]: {
                frameRate: 30,
                audioBitrate: 40000,
                resolution: '640x480',
            },
            [VideoQuality.High]: {
                frameRate: 30,
                audioBitrate: 40000,
                resolution: '1280x720',
            },
        };
    }
    get qualityConfigCustomization() {
        return {
            ...AUDIO_TYPE_CONFIGURATION_MAP[this.audioType],
        };
    }
    getScreensharePublisher() {
        return this.OTSession.screensharePublisher;
    }
    getScreensharePublisherVideoElement() {
        return this.getScreensharePublisher()?.videoElement();
    }
    async initScreenPublisher(constraints) {
        try {
            const element = document.querySelector(`#${this.localScreenPublisherId}`);
            let props = {
                videoSource: 'screen',
                maxResolution: { width: 1920, height: 1080 },
                width: element.offsetWidth,
                height: element.offsetHeight,
                insertDefaultUI: false,
                frameRate: 7,
            };
            if (constraints) {
                const stream = await navigator.mediaDevices.getDisplayMedia(constraints);
                const videoTrack = stream.getVideoTracks()[0];
                const audioTrack = stream.getAudioTracks()[0];
                props = {
                    videoSource: videoTrack,
                    audioSource: audioTrack,
                    insertDefaultUI: false,
                };
            }
            const oldChildren = angular.element(element).children();
            const screensharePublisher = this.OTSession.initPublisher(null, props, (err) => {
                if (err) {
                    this.$rootScope.$emit('otScreenPublisher:error', err);
                    return;
                }
                const isSharingHasAudio = Boolean(this.OTSession.screensharePublisher.getAudioSource());
                if (isSharingHasAudio) {
                    this.selfSharingMicRendererInit();
                }
                this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.SCREEN_SHARE_STARTED, { withAudio: isSharingHasAudio });
            });
            angular.element(element).append(oldChildren);
            screensharePublisher.on('videoElementCreated', (event) => {
                angular.element(element).append(event.element);
            });
            if (this.OTSession.isSessionConnected()) {
                this.OTSession.session.publish(screensharePublisher);
            }
            screensharePublisher.on(VideoProviderEvents.StreamDestroyed, (event) => {
                if (!this.globalUi.ui.isRestartingCall ||
                    event.reason === 'mediaStopped') {
                    this.$rootScope.$emit('screenshareStopped', event);
                }
                else {
                    event.preventDefault();
                }
            });
            this.OTSession.screensharePublisher = screensharePublisher;
        }
        catch (err) {
            this.$rootScope.$emit('otScreenPublisher:error', err);
            this.destroyScreenPublisher();
        }
    }
    selfSharingMicRendererInit() {
        this.OTSession.screensharePublisher.on('loaded', () => {
            const volumeListener = new TokboxVolumeListener(this.OTSession.screensharePublisher);
            this.selfMicrophoneRenderer.initCanvasContexts();
            const microphoneRenderer = new TokboxMicrophoneRenderer('screen', this.$rootScope);
            microphoneRenderer.initCanvasContexts();
            volumeListener.events.on('update', (level) => {
                this.screenVolumeLevel = level;
                microphoneRenderer.renderMicrophone(level);
            });
        });
    }
    destroyScreenPublisher() {
        if (this.OTSession.session) {
            this.OTSession.session.unpublish(this.OTSession.screensharePublisher);
        }
        else {
            this.OTSession.screensharePublisher.destroy();
        }
        this.OTSession.screensharePublisher = null;
        const element = document.getElementById(this.localScreenPublisherId);
        angular.element(element).html('');
    }
    async initPublisher(config = {}) {
        this.isPublisherLoading = true;
        this.$ngRedux.dispatch(call.actions.toggleSelfPublisherError(false));
        const element = document.getElementById(this.localVideoPublisherId);
        const sourceConfig = {};
        // Cache checking: double check availability of stored device info
        // Reason: there are could be disconnected devices in cached deviceInfo
        const hasNoDevices = devices.selectors.hasNoDevices(this.$ngRedux.getState());
        if (hasNoDevices) {
            this.isPublisherLoading = false;
            this.$ngRedux.dispatch(call.actions.addSelfPublisherError({
                type: call.constants.SelfPublisherErrorType.NoDevices,
            }));
            this.updateState({ cameraStatus: ConnectionStateCameraStatus.Error });
            return Promise.reject({
                name: 'NotFoundError',
            });
        }
        // EOF: Cache checking
        const deviceInfo = devices.selectors.getCurrentDevicesInfo(this.$ngRedux.getState());
        if (deviceInfo.microphoneId) {
            sourceConfig.audioSource = deviceInfo.microphoneId;
        }
        if (deviceInfo.cameraId) {
            sourceConfig.videoSource = deviceInfo.cameraId;
        }
        /*
          @Issue       https://doxyme.atlassian.net/browse/CS-2256
          @Description To be able to have only mic or camera allowed
         */
        let hasCameraAccess;
        let hasMicrophoneAccess;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
            this.$ngRedux.dispatch(devices.actions.mediaAccessAllowed());
            hasCameraAccess = true;
        }
        catch (e) {
            this.$ngRedux.dispatch(call.actions.addSelfPublisherError({
                type: call.constants.SelfPublisherErrorType.GetUserVideo,
                message: e.name,
            }));
            this.updateState({ cameraStatus: ConnectionStateCameraStatus.Error });
            sourceConfig.videoSource = null;
            sourceConfig.publishVideo = false;
            hasCameraAccess = false;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
            this.$ngRedux.dispatch(devices.actions.mediaAccessAllowed());
            hasMicrophoneAccess = true;
        }
        catch (e) {
            this.$ngRedux.dispatch(call.actions.addSelfPublisherError({
                type: call.constants.SelfPublisherErrorType.GetUserAudio,
                message: e.name,
            }));
            sourceConfig.audioSource = null;
            sourceConfig.publishAudio = false;
            hasMicrophoneAccess = false;
        }
        this.$ngRedux.dispatch(devices.actions.setCameraAccess(hasCameraAccess));
        this.$ngRedux.dispatch(devices.actions.setMicrophoneAccess(hasMicrophoneAccess));
        // Reject if initiating the publisher does not make sense
        if (!hasCameraAccess && !hasMicrophoneAccess) {
            const err = { name: OTPublisherErrors.NotAllowedError };
            this.$rootScope.$emit(RootScopeEvents.OT_PUBLISHER_ERROR, err);
            this.otPublisherErrorHandler.onError(err);
            this.isPublisherLoading = false;
            this.updateState({ cameraStatus: ConnectionStateCameraStatus.Blocked });
            this.$ngRedux.dispatch(call.actions.addSelfPublisherError({
                type: call.constants.SelfPublisherErrorType.NoAudioAndVideoAccess,
            }));
            return Promise.reject(err);
        }
        store.dispatch(initBackgroundEffect());
        if (this.isInCall && !this.ui.hasVideo) {
            sourceConfig.publishVideo = false;
        }
        config.publishAudio =
            typeof config.publishAudio !== 'boolean'
                ? this.OTSession.ui.hasAudio
                : config.publishAudio;
        config.publishVideo =
            typeof config.publishVideo !== 'boolean'
                ? this.OTSession.ui.hasVideo
                : config.publishVideo;
        const props = Object.assign(config, sourceConfig, {
            insertDefaultUI: false,
            showControls: false,
            videoFilter: getBackgroundEffect(store.getState()).filter,
            /**
             * We rely on our own device management logic, so we don't want to use the one from Vonage
             */
            disableAudioInputDeviceManagement: true,
            /**
             * This will only start/stop working when captions are enabled/disabled for the session via startLiveCaptions/stopLiveCaptions API
             */
            publishCaptions: true,
        }, {
            ...this.qualityConfig[this.currentVideoQuality],
            ...this.qualityConfigCustomization,
        });
        /*
         * if the browser is ios or ipad, then set a private variable
         * inside OpenTok to give h264 priority.
         * Ideally we would do this only for 1:1 calls but at this point we don't
         * know if this will be routed or relayed
         */
        if (this.applyPriorityCodec && this.priorityVideoCodec) {
            props._priorityVideoCodec = this.priorityVideoCodec;
        }
        const oldChildren = element ? angular.element(element).children() : null;
        this.OTSession.publisher?.destroy();
        this.debug('props:', props);
        const publisher = this.OTSession.initPublisher(null, props, (err) => {
            if (err) {
                this.otPublisherErrorHandler.onError(err);
                this.debug('otPublisherError:', err);
                this.$ngRedux.dispatch(call.actions.addSelfPublisherError({
                    type: call.constants.SelfPublisherErrorType.OTInitPublisher,
                    message: err.name,
                }));
                this.$rootScope.$emit(RootScopeEvents.OT_PUBLISHER_ERROR, err);
                this.updateState({ cameraStatus: ConnectionStateCameraStatus.Error });
            }
            else {
                this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.PUBLISHER_SUCCESS, this.ui);
                this.updateState({ cameraStatus: ConnectionStateCameraStatus.Success });
                this.otPublisherErrorHandler.onSuccess();
                this.debug('mediaAccessAllowed');
                this.$ngRedux.dispatch(devices.actions.mediaAccessAllowed());
            }
        });
        this.OTSession.publisher = publisher;
        new TokboxPublisherListener(this.$injector, publisher);
        //FIXME: think how to handle it better (required for MOBILE-UI)
        if (element) {
            angular.element(element).append(oldChildren);
        }
        const deferred = createDeferred();
        publisher.videoElementPromise = deferred.promise;
        publisher.on('videoElementCreated', (event) => {
            //FIXME: think how to handle it better (required for MOBILE-UI)
            if (element) {
                angular.element(element).append(event.element);
            }
            deferred.resolve(publisher.videoElement());
            // fix https://bugs.chromium.org/p/chromium/issues/detail?id=1139900
            if (this.Browser.isAndroidChrome86()) {
                const videoTrack = event.element.srcObject.getVideoTracks()[0];
                if (videoTrack) {
                    videoTrack.onended = () => {
                        setTimeout(() => {
                            if (!androidChrome86FixApplied && this.ui.hasVideo) {
                                androidChrome86FixApplied = true;
                                this._onConfigUpdate();
                            }
                        });
                    };
                }
            }
            this.emit(VideoProviderEvents.LocalStream, publisher.videoElement().srcObject);
            // toggle loader on video change
            event.element.addEventListener('emptied', () => {
                store.dispatch(call.actions.setSelfPreviewLoading(true));
            });
            event.element.addEventListener('loadeddata', () => {
                store.dispatch(call.actions.setSelfPreviewLoading(false));
            });
            if (this.Browser.isMobileSafari() || this.Browser.isIPad()) {
                const CHECK_IN_TIMEOUT = 2000;
                const pause = () => {
                    // it will restart camera and should fix freezing in most cases
                    setTimeout(() => {
                        event.element.pause();
                    }, CHECK_IN_TIMEOUT);
                    event.element.removeEventListener(HTMLMediaElementEvents.LoadedData, pause);
                };
                event.element.addEventListener(HTMLMediaElementEvents.LoadedData, pause);
            }
        });
        publisher.on('destroyed', (event) => {
            setTimeout(() => element.querySelector('video')?.remove());
            // try to re init, this event can be fired when phone goes to the background
            this.updateState({ cameraStatus: ConnectionStateCameraStatus.Error });
            if (event.reason === 'mediaStopped') {
                this.initPublisher({
                    publishAudio: this.OTSession.ui.hasAudio,
                    publishVideo: this.OTSession.ui.hasVideo,
                });
            }
        });
        let previousEvent;
        [
            'accessAllowed',
            'accessDenied',
            'accessDialogClosed',
            'accessDialogOpened',
        ].forEach((event) => {
            publisher.on(event, () => {
                if (previousEvent === 'accessDenied' && event === 'accessDialogOpened')
                    return;
                previousEvent = event;
                this.$rootScope.$emit(event);
            });
        });
        publisher.on('loaded', () => {
            const MAX_WAITING_TIME = 1000;
            this.$timeout(() => {
                if (!devices.selectors.hasMicrophoneAccess(this.$ngRedux.getState()))
                    return;
                this.initMicRenderer(publisher);
            }, MAX_WAITING_TIME);
        });
        if (this.OTSession.isSessionConnected()) {
            this.OTSession.session.publish(publisher);
        }
        publisher.on('streamCreated', () => {
            this.$ngRedux.dispatch(call.actions.toggleSelfPublisherError(false));
            /**
             * If captions are open, create a self-captions subscriber.
             * If will let captions to continue working on every self media refresh caused by
             * user actions (like troubleshooting) or some internal logic.
             */
            const currentState = this.$ngRedux.getState();
            const isCaptionsOpen = getIsCaptionsOpen(currentState);
            if (isCaptionsOpen) {
                this.createSelfCaptionsSubscriber();
            }
        });
        publisher.on('streamDestroyed', (event) => {
            event.preventDefault();
            if (event.reason === 'mediaStopped') {
                const toastAction = isDoctor()
                    ? call.actions.showSelfPublisherErrorToastProvider()
                    : call.actions.showSelfPublisherErrorToastParticipant();
                this.$ngRedux.dispatch(call.actions.toggleSelfPublisherError(true));
                this.$ngRedux.dispatch(toastAction);
                this.initPublisher();
            }
        });
        return this.waitForPublisher().finally(() => {
            this.isPublisherLoading = false;
        });
    }
    /**
     * @description Call to refresh Stream(destroy and resubscribe)
     */
    recreateStream(stream) {
        if (!this.isInCall)
            return;
        if (!stream)
            return;
        const otStream = stream.stream;
        if (!otStream)
            return;
        const connection = stream.personConnection;
        const oldStream = connection.removeStream(stream);
        // Stream can possible be already destroyed or removed
        if (!oldStream)
            return;
        const newStream = connection.createStream(otStream);
        this.updateStreams();
        this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.RECREATE_STREAM, newStream);
        this.$rootScope.$digest();
        return newStream;
    }
    initMicRenderer(publisher) {
        const volumeListener = new TokboxVolumeListener(publisher);
        this.selfMicrophoneRenderer.initCanvasContexts();
        volumeListener.events.on('update', (level) => {
            this.volumeLevel = level;
            this.selfMicrophoneRenderer.renderMicrophone(level);
        });
    }
    onAudioConfigUpdate() {
        const microphoneId = devices.selectors.getCurrentMicrophoneId(this.$ngRedux.getState());
        /**
         * Prevent echo after changing mic in mobile Safari
         * @description https://doxyme.atlassian.net/browse/CS-4580
         * @todo https://doxyme.atlassian.net/browse/CS-5176
         */
        this.Browser.isMobileSafariWithEcho()
            ? this.onConfigUpdate()
            : this.OTSession.publisher.setAudioSource(microphoneId);
    }
    _onConfigUpdate() {
        this.debug('_onConfigUpdate');
        if (this.OTSession.publisher) {
            this.OTSession.publisher.on('destroyed', () => {
                this.initPublisher();
            });
            this.OTSession.publisher.destroy();
        }
    }
    _getAudioVideoState() {
        return this.OTSession.ui;
    }
    _initSession() {
        this.preparePublisherForSession();
        const sessionPromise = this.OTSession.init(this.apiKey, this.channel, this.token, this.sessionOptions);
        this._registerSessionEvents();
        return sessionPromise;
    }
    preparePublisherForSession() {
        // go to default codec for all routed sessions (group calls)
        if (this.getMediaMode() === MediaModes.ROUTED) {
            this.applyPriorityCodec = false;
            this.$log.debug('applyPriorityCodec = false');
            this._onConfigUpdate();
        }
    }
    get sessionOptions() {
        const isIpWhiteList = _.get(this.sessionConfig, 'ipWhiteList', false);
        const options = {
            ipWhitelist: isIpWhiteList,
        };
        if (this.Browser.isWithBuggedFirewall()) {
            options.iceConfig = {
                includeServers: 'all',
                transportPolicy: 'relay',
                customServers: [],
            };
        }
        return options;
    }
    _joinSession(config) {
        this.preparePublisherForSession();
        const sessionInitialised = !!window.OT.sessions.get(this.channel);
        const apiKey = config.apiKey || this.getApiKey();
        let sessionPromise = Promise.resolve();
        if (sessionInitialised) {
            // TODO this.OTSession.init
            this.OTSession.session = window.OT.initSession(apiKey, this.channel, this.sessionOptions);
            sessionPromise = new Promise((resolve, reject) => {
                this.OTSession.session.connect(this.token, (err) => {
                    if (err)
                        return reject(err);
                    resolve();
                });
            });
        }
        else {
            sessionPromise = this.OTSession.init(apiKey, this.channel, this.token, this.sessionOptions);
            this._registerSessionEvents();
        }
        return sessionPromise;
    }
    /**
     * @description Handling exceptions thrown inside opentok.
     *              ** Attention **
     *              A quick way to check is to run in the console during a call:
     *              OT.emit('exception', { code: 42, name: 'OT_YOUR_TESTED_NAME', message: 'your message' });
     */
    OTExceptionHandler() {
        const EXCEPTION_HANDLERS = {
            // those that previously led to warning message
            ignoreWarning: {
                action: (reason) => {
                    this.$rootScope.$emit('activeProvider:ignoreWarning', reason);
                },
                type: 'ignoreWarning',
            },
            // those that previously led to dropped call
            ignoreError: {
                action: (reason) => {
                    this.$rootScope.$emit('activeProvider:ignoreError', reason);
                },
                type: 'ignoreError',
            },
            // those that previously led to dropped call
            republishOnError: {
                // TODO: Add some debounce timeout after testing
                action: (reason) => {
                    this._onConfigUpdate();
                    this.$rootScope.$emit('activeProvider:republishOnError', reason);
                },
                type: 'republishOnError',
            },
            // those that previously led to warning message
            republishOnWarning: {
                // TODO: Add some debounce timeout after testing
                action: (reason) => {
                    this._onConfigUpdate();
                    this.$rootScope.$emit('activeProvider:republishOnWarning', reason);
                },
                type: 'republishOnWarning',
            },
        };
        OT.on &&
            OT.on('exception', async (event) => {
                let exceptionHandler;
                const err = event.error || {};
                let level = 'error';
                let reason;
                let message;
                if (event.message.match('Access Denied') ||
                    event.message.match('Unable to send signal') ||
                    event.message.match('GetUserMedia'))
                    return;
                switch (event.code) {
                    case 2001:
                        message = 'connection-failed';
                        reason = 'unexpectedServerError';
                        /**
                         * @description 40 packet loss, When you change video quality LOW HIGH LOW HIGH ...
                         * @event       cancelable: false
                                        code: 2001
                                        component: Publisher {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        error:
                                          code: 500
                                          message: "Not connected."
                                        message: "Not connected."
                                        target: Publisher {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        title: "Unexpected Server Response"
                        * @solution    STOP
                        */
                        break;
                    case 1553:
                    case 1554:
                        message = this.Browser.isIosWithBuggedFirewall()
                            ? 'firewall-ios-15'
                            : 'firewall';
                        reason = 'firewall';
                        break;
                    case 1013:
                        /**
                         * @description When you change video quality LOW HIGH LOW HIGH ...
                         * @event       cancelable: false
                                          code: 1501
                                          component: Subscriber {id: null, widgetId: "16736cdf-9350-46e5-a3e9-b8f3d1225e86", session: null, stream: null, streamId: null, …}
                                          error: OTErrorClass
                                            code: 1013
                                            message: "OT.Subscriber while playing stream: Cannot call off, NativeVideoElementWrapper is destroyed"
                                            name: "OT_MEDIA_ERR_DECODE"
                                          message: "OT.Subscriber while playing stream: Cannot call off, NativeVideoElementWrapper is destroyed"
                                          target: Subscriber {id: null, widgetId: "16736cdf-9350-46e5-a3e9-b8f3d1225e86", session: null, stream: null, streamId: null, …}
                                          title: "Unable to Subscribe"
                         * @solution    IGNORE
            
                         * @description When you change video quality LOW HIGH LOW HIGH ...
                         * @event       cancelable: false
                                        code: 1013
                                        component: Publisher {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        error: OTErrorClass
                                          code: 1013
                                          message: "OT.Publisher PeerConnection Error: Cannot call off, NativeVideoElementWrapper is destroyed"
                                          name: "OT_CONNECT_FAILED"
                                        message: "OT.Publisher PeerConnection Error: Cannot call off, NativeVideoElementWrapper is destroyed"
                                        target: Publisher {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        title: "Connection Failed"
                        * @solution    IGNORE
                        */
                        message = 'connection-failed';
                        reason = '1013';
                        exceptionHandler = EXCEPTION_HANDLERS['ignoreError'];
                        break;
                    case 1500:
                        if (err.name === OT_ERRORS.HARDWARE_UNAVAILABLE) {
                            /**
                             * @description When you select specific mic device and then disconnect it.
                             * @event       cancelable: false
                                            code: 1500
                                            component: Publisher {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                            error: OTErrorClass
                                              code: 1500
                                              message: "The selected voice or video devices are unavailable. Verify that the chosen devices are not in use by another application. (getUserMedia error: NotReadableError)"
                                              name: "OT_HARDWARE_UNAVAILABLE"
                                            message: "GetUserMedia"
                                            target: Publisher {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                            title: "Unable to Publish"
                             * @solution1 Use another available device
                                          const deviceInfo = await this.Devices.checkAvailability();
                                          this.debug('OT_HARDWARE_UNAVAILABLE', deviceInfo.devices)
                                          this.deviceSettings.setInfo(deviceInfo.devices);
              
                                          Solution above does not work.
                                             Browser recognize disconnected device as existing one and cannot publish the stream.
                                             We have to manually switch to another device but there is no way to know which device is disconnected
                                             navigator.mediaDevices.enumerateDevices() - gives incorrect results
                                                                                         https://prnt.sc/ui98sd
                                             const deviceInfo = await this.Devices.checkAvailability();
                                             this.debug('OT_HARDWARE_UNAVAILABLE', deviceInfo.devices)
                                             this.deviceSettings.setInfo({});
                             * @solution2 STOP
                             * @todo      HANDLE THIS
                            */
                            message = 'connection-failed';
                            reason = 'hardwareUnavailable';
                            break;
                        }
                        /**
                         * @description When you change video quality LOW HIGH LOW HIGH ...
                         * @event       cancelable: false
                                        code: 1500
                                        component: Publisher {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        error: Error: Did not pass Error as second argument: function otError(name, plainError, code) { if (names.indexOf(name) === -1) { return new Error("Attempt to use invalid error name (" + name + "). Original message: " + plainError.message); } if (!(plainError instanceof Error || /^\[object .*Error\]$/.test(Object.prototype.toString.call(plainError)))) { return new Error("Did not pass Error as second argument: " + plainError); } // OTErrorClass is the deprecated OT.Error class from the docs. const error = new OTErrorClass(undefined, plainError.message); error.name = name; if (!plainError.stack) { try { throw plainError; } catch (e) {// IE populates the error's .stack when it is thrown, nothing to do here } } error.stack = plainError.stack; if (code) { error.code = code; } errorReporting.send(error); return error; } at otError (webpack:///./node_modules/@opentok/client/dist/js/opentok.js?:2324:14) at onStreamAvailableError (webpack:///./node_modules/@opentok/client/dist/js/opentok.js?:15792:21) at userMediaError (webpack:///./node_modules/@opentok/client/dist/js/opentok.js?:15890:9) at invokeCallback (webpack:///./node_modules/@opentok/client/dist/js/opentok.js?:512:15) at publish (webpack:///./node_modules/@opentok/client/dist/js/opentok.js?:495:7) at publishRejection (webpack:///./node_modules/@opentok/client/dist/js/opentok.js?:436:3) at MutationObserver.flush (webpack:///./node_modules/@opentok/client/dist/js/opentok.js?:232:5)
                                        message: "Did not pass Error as second argument: function otError(name, plainError, code) {↵    if (names.indexOf(name) === -1) {↵      return new Error("Attempt to use invalid error name (" + name + "). Original message: " + plainError.message);↵    }↵↵    if (!(plainError instanceof Error || /^\[object .*Error\]$/.test(Object.prototype.toString.call(plainError)))) {↵      return new Error("Did not pass Error as second argument: " + plainError);↵    } // OTErrorClass is the deprecated OT.Error class from the docs.↵↵↵    const error = new OTErrorClass(undefined, plainError.message);↵    error.name = name;↵↵    if (!plainError.stack) {↵      try {↵        throw plainError;↵      } catch (e) {// IE populates the error's .stack when it is thrown, nothing to do here↵      }↵    }↵↵    error.stack = plainError.stack;↵↵    if (code) {↵      error.code = code;↵    }↵↵    errorReporting.send(error);↵    return error;↵  }"
                                        message: "GetUserMedia"
                                        target: Publisher {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        title: "Unable to Publish"
                         * @solution    REPUBLISH
                        */
                        if (err.message === 'GetUserMedia') {
                            reason = 'getUserMedia';
                        }
                        /**
                         * @description When you just on a bad conditions 250kbs 30%packetloss
                         * @event       cancelable: false
                                        code: 1500
                                        component: Publisher {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        error: undefined
                                          message: "ICEWorkflow"
                                        target: Publisher {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        title: "Unable to Publish"
                                        type: "exception"
                         * @solution    REPUBLISH
                        */
                        if (err.message === 'ICEWorkflow') {
                            reason = 'ICEWorkflow';
                        }
                        message = 'connection-failed';
                        exceptionHandler = EXCEPTION_HANDLERS['republishOnError'];
                        break;
                    case 1501:
                        /**
                         * @description 40 packet loss, When you change video quality LOW HIGH LOW HIGH ...
                         * @event       cancelable: false
                                        code: 1501
                                        component: Session {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        error: OTErrorClass
                                          code: 1501
                                          message: "Unable to subscribe to stream in a reasonable amount of time"
                                          name: "OT_TIMEOUT"
                                        message: "Unable to subscribe to stream in a reasonable amount of time"
                                        target: Session {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        title: "Unable to Subscribe"
                                        type: "exception"
                        * @solution    IGNORE
                        */
                        message = 'connection-failed';
                        reason = 'otTimeout';
                        exceptionHandler = EXCEPTION_HANDLERS['ignoreWarning'];
                        break;
                    case 1006:
                        message = 'connection-failed';
                        reason = '1006';
                        break;
                    case 1008:
                        message = 'connection-time-out';
                        level = 'warning';
                        break;
                    default:
                        message = 'catch-all';
                        level = 'warning';
                        /**
                         * @description When you change video quality LOW HIGH LOW HIGH ...
                         * @event       cancelable: false
                                        code: undefined
                                        component: Session {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                                        error: OTErrorClass
                                          code: undefined
                                          message: "Stream was destroyed before it could be subscribed to"
                                          name: "OT_STREAM_DESTROYED"
                                        message: "Stream was destroyed before it could be subscribed to"
                                        target: Session {on: ƒ, off: ƒ, once: ƒ, dispatchEvent: ƒ, trigger: ƒ, …}
                        * @solution    IGNORE
                        */
                        if (err.name === OT_ERRORS.STREAM_DESTROYED) {
                            reason = err.name;
                            exceptionHandler = EXCEPTION_HANDLERS['ignoreWarning'];
                        }
                        if (err.name === 'publisherInSession') {
                            exceptionHandler = EXCEPTION_HANDLERS['republishOnWarning'];
                            reason = 'publisherInSession';
                        }
                        if (event.message && event.message.includes('firewall')) {
                            message = this.Browser.isIosWithBuggedFirewall()
                                ? 'firewall-ios-15'
                                : 'firewall';
                            reason = 'firewall';
                        }
                    /**
                     * @TODO reproduce and handle
                     * Chrome - Provider, Safari - Patient
                     * “OT.Publisher PeerConnection Error: Error while setting RemoteDescription InvalidStateError: Failed to execute ‘setRemoteDescription’ on ‘RTCPeerConnection’: Failed to set remote answer sdp: Called in wrong state: kStable”
                     */
                }
                /**
                 * Vlad Palii - 2020.05.07
                 * https://github.com/opentok/opentok-network-test-js
                 * Undefined OTSession.session caused only when running opentok-network-test-js
                 * Decided to omit this error
                 */
                if (!this.OTSession.session)
                    return false;
                this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.OT_EXCEPTION, event);
                console.error('tokbox.exception', {
                    action: 'tokbox.exception',
                    sessionId: this.OTSession.session.sessionId,
                    user: this.LoopBackAuth.currentUserData
                        ? _.pick(this.LoopBackAuth.currentUserData, ['id'])
                        : 'patient',
                    error: err && err.message,
                });
                if (exceptionHandler) {
                    this.debug('exceptionHandler', exceptionHandler.type, event);
                    exceptionHandler.action(reason);
                }
                else {
                    this.debug('exceptionHandler', 'exception', event, level, reason, message, err);
                    this.emit(VideoProviderEvents.Exception, {
                        level: level,
                        message: message,
                        reason,
                    });
                }
            });
    }
    _registerOTEvents() {
        this.$rootScope.$on('otSubscriberCreated', async ($e, subscriber) => {
            this.OTSession.subscribers.push(subscriber);
            if (_.get(subscriber, 'stream.videoType') !== 'screen') {
                this.setSink(subscriber.videoElement());
            }
        });
        this.$rootScope.$on('otSubscriberDestroyed', ($e, subscriber) => {
            _.pull(this.OTSession.subscribers, subscriber);
        });
        this.OTExceptionHandler();
    }
    _registerSessionEvents() {
        this.OTSession.session.on({
            streamPropertyChanged: (event) => {
                if (event.changedProperty === 'hasVideo') {
                    // when we turn on video double check current quality/stream
                    if (event.newValue && !this.isLocalQualityCorrect()) {
                        this.onVideoQualityChange();
                    }
                }
                // FIXME: for updating streams properties in react!!
                if (event.stream.id !== this.selfConnection.id) {
                    this.updateStreams();
                }
            },
            signal: (e) => {
                if (this.isSelfConnection(e.from.connectionId)) {
                    return;
                }
                this.tokboxSignalService.onSignal(e);
            },
            sessionConnected: () => {
                this.emit(VideoProviderEvents.SessionConnected);
                this.$rootScope.$emit('activeProvider:sessionConnected');
                /*
                  remove all previous connections
                  when session disconnected by network we still keep all previous people in freeze
                  so here we have to reset them
                */
                this.resetConnections();
            },
            sessionDisconnected: (e) => {
                const amIDoctor = this.pn.isDoctor();
                const participants = _.clone(this.peopleCollection.models);
                const onSessionDisconnect = () => {
                    this.resetConnections();
                    this.OTSession.resetPublishers();
                    this.emit(VideoProviderEvents.SessionDisconnected, {
                        reason: e.reason,
                    });
                    this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.SESSION_DISCONNECTED, {
                        reason: e.reason,
                        participants: participants,
                        currentVideoQuality: this.currentVideoQuality,
                    });
                };
                if (e.reason === 'networkDisconnected' && !amIDoctor) {
                    this.setIsReconnecting(true);
                    this.selfConnection.onDestroy();
                    this.$rootScope.$emit('activeProvider:reconnectingStart');
                    this.debug('RECONNECTING');
                    this._joinSession()
                        .catch(() => {
                        this.setIsReconnecting(false);
                        this.$rootScope.$emit('activeProvider:reconnectingError');
                        onSessionDisconnect();
                    })
                        .then(() => {
                        if (!this.isReconnecting) {
                            this._endSession();
                            this.$rootScope.$emit('activeProvider:reconnectingSuccessAfterTimeout');
                            return;
                        }
                        else {
                            this.setIsReconnecting(false);
                        }
                        this.$rootScope.$emit('activeProvider:reconnectingSuccess');
                    })
                        .finally(() => this.pn.syncAllRooms());
                    // when it is not reconnected during MAX_TIME_TO_RECONNECT sec we have to stop the call.
                    setTimeout(() => {
                        if (this.isReconnecting) {
                            this._endSession();
                            onSessionDisconnect();
                        }
                    }, MAX_TIME_TO_RECONNECT);
                }
                else {
                    onSessionDisconnect();
                }
            },
            connectionCreated: (e) => {
                if (e.connection.id === this.OTSession.session.connection.connectionId) {
                    this.selfConnection = new TokboxSelfConnection(this.$injector, e.connection, this.pn);
                }
                else {
                    /**
                     *  @issue       https://doxyme.atlassian.net/browse/CS-2124
                     *  @description When doctor click startCall btn multiple times it can create multiple session
                     *               so as the result it appears as Undetermined in patient queue and miniature
                     *  @todo        Now it is more like a hot fix to disable weird person from queue
                     *               but it should be impossible to have multiple sessions in parallel
                     *               After refactoring to REDUX we have to cover this with @tests
                     *  */
                    const selfConnectionId = _.get(e, 'target.connection.connectionId');
                    if (selfConnectionId === e.connection.id) {
                        console.error('Self connection from another session', {
                            currentSessionId: this.OTSession.session.id,
                            sessionId: e.target['id'],
                        });
                        return;
                    }
                    const data = new QueryString(e.connection.data).toObject();
                    const person = this.findPnPersonByState({ uid: data.uuid });
                    let personConnection = this.connections.find((c) => c.person.uuid === person.uuid);
                    if (personConnection) {
                        if (personConnection.isDestroyed) {
                            this.$rootScope.$emit('activeProvider:personReconnectedSuccessfully');
                        }
                    }
                    else {
                        personConnection = new TokboxPersonConnection(this.$injector, this.OTSession.session, e.connection, person, this.$ngRedux);
                        this.connections.push(personConnection);
                    }
                    personConnection.onConnection(e.connection);
                    this.updateStreams();
                    this.$rootScope.$digest();
                    this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.CONNECTION_CREATED, {
                        uuid: personConnection.person.uuid,
                        onlyChat: personConnection.person.state.onlyChat,
                        systemInfo: personConnection.person.getSystemInfo(),
                    });
                    this.emit(VideoProviderEvents.ConnectionCreated, personConnection);
                    this.onPersonConnection(personConnection);
                }
            },
            streamCreated: (e) => {
                const connection = _.find(this.connections, {
                    id: e.stream.connection.id,
                });
                if (!connection) {
                    this.$rootScope.$emit('activeProvider:stopWhenNoConnectionForTheStream');
                    console.error('stopWhenNoConnectionForTheStream');
                    this._endSession();
                }
                const stream = connection.createStream(e.stream);
                this.updateStreams();
                this.$rootScope.$digest();
                this.emit(VideoProviderEvents.StreamCreated, stream);
            },
            streamDestroyed: (e) => {
                const connection = _.find(this.connections, {
                    id: e.stream.connection.id,
                });
                if (!connection) {
                    console.error('streamDestoryedWithoutConnection');
                    return;
                }
                let removedStream = _.find(connection.streams, {
                    id: e.stream.id,
                });
                if (e.reason !== 'networkDisconnected') {
                    removedStream = connection.removeStream(e.stream);
                }
                this.updateStreams();
                this.$rootScope.$digest();
                if (removedStream)
                    this.emit(VideoProviderEvents.StreamDestroyed, removedStream);
            },
            connectionDestroyed: (e) => {
                const amIDoctor = this.pn.isDoctor();
                const connection = _.find(this.connections, {
                    id: e.connection.connectionId,
                });
                if (!connection) {
                    console.error('noConnectionOnConnectionDestroyed');
                    return;
                }
                connection.onDestroy();
                const onConnectionDestroy = () => {
                    _.remove(this.connections, { id: e.connection.connectionId });
                    this.updateStreams();
                    this.$rootScope.$digest();
                    this.emit(VideoProviderEvents.ConnectionDestroyed, connection, e.reason);
                    this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.CONNECTION_DESTROYED, {
                        uuid: connection.person.uuid,
                        reason: e.reason,
                    });
                };
                if (e.reason !== 'networkDisconnected' || !amIDoctor) {
                    onConnectionDestroy();
                }
                else {
                    this.$rootScope.$emit('activeProvider:connectionWatingToReconnect');
                    setTimeout(() => {
                        const existingConnection = _.find(this.connections, {
                            id: e.connection.connectionId,
                        });
                        if (existingConnection) {
                            this.$rootScope.$emit('activeProvider:connectionDestroyByTimeout');
                            onConnectionDestroy();
                        }
                    }, MAX_TIME_TO_RECONNECT);
                }
            },
        });
    }
    /**
     *
     * @description Actually right now we cannot force remove patient from the call.
     *              To implement this we have to make doctor @moderaror of session.
     *              For now we just remove already destoryed connections to prevent situation
     *              when person will not see everybody in the call.
     */
    forceDisconnectPerson(person) {
        const removedConnections = _.remove(this.connections, (c) => c.isDestroyed && c.person.uuid === person.uuid);
        if (removedConnections.length > 0) {
            this.debug('removedDestoryedConnections');
            this.$rootScope.$emit('activeProvider:destroyedConnectionForceRemoved');
            this.updateStreams();
            this.$rootScope.$evalAsync();
        }
    }
    resetConnections() {
        this.connections.splice(0, this.connections.length);
        this.updateStreams();
        this.$rootScope.$evalAsync();
    }
    _endSession() {
        this.setIsReconnecting(false);
        this.$log.debug('tokbox-provider:_endSession');
        /**
         *  @description There is a case when you call phone.stop() by exception
         *               but in a bad connection you will never receive sessoinDisconnected event.
         *               we have to clear connections manually
         *  @warning     I am not sure if we call this method and a good connection came back
         *               the session will not continue to publishing
         *
         *  @description If a call was successfully started after reconnect but should be stopped by timeout anyway
         *               This will prevent puping up and closing call.
         *  @warning     By clarning connections that way we cannot be sure that they are no more in call(maybe for some issues person not be disconnected).
         *               But in the other hand Tokbox does not always gives us correct sesionDisconnected event and call can proceed event when it is disconnected.
         **/
        this.resetConnections();
        // Clean up hidden subscriber for self-captions
        this.destroySelfCaptionsSubscriber();
        return this.OTSession.disconnect();
    }
    async stop() {
        this._endSession();
        await this.destroyPublishers();
    }
    isSessionConnected() {
        return this.OTSession.isSessionConnected();
    }
    getPublisher() {
        return this.OTSession.publisher;
    }
    getCurrentMediaConfig() {
        const deviceInfo = devices.selectors.getCurrentDevicesInfo(this.$ngRedux.getState());
        let video = _.cloneDeep(this.qualityMediaConfig[this.currentVideoQuality]);
        if (deviceInfo.cameraId) {
            Object.assign(video, { deviceId: { ideal: deviceInfo.cameraId } });
        }
        if (deviceInfo.facingMode) {
            Object.assign(video, { facingMode: deviceInfo.facingMode });
        }
        if (this.globalUi.hasMediaAccess) {
            if (!this.globalUi.hasCameraAccess)
                video = false;
        }
        return { video };
    }
    onVideoQualityChange() {
        const { video } = this.getCurrentMediaConfig();
        if (!this.localStream)
            return;
        const videoTrack = this.localStream.getVideoTracks()[0];
        if (!videoTrack)
            return;
        if (_.isObject(video)) {
            this.tokboxSignalService.signalVideoConstraintsApplied();
            videoTrack
                .applyConstraints(video)
                .then(() => {
                this.$log.debug('tokbox-provider:applyConstraints succeed');
            })
                .catch((err) => {
                console.error('videoTrack.applyConstraints() error:', err, video);
            });
        }
    }
    onConnectionStateChange() {
        this.tokboxSignalService.updateState(this.connectionState);
    }
    onPersonConnection(personConnection) {
        this.tokboxSignalService.updateState(this.connectionState, personConnection.connection);
    }
    isLocalQualityCorrect() {
        if (!this.localStream)
            return false;
        const videoTrack = this.localStream.getVideoTracks()[0];
        if (!videoTrack)
            return false;
        const constraints = videoTrack.getConstraints();
        const currentMediaQuality = this.qualityMediaConfig[this.currentVideoQuality];
        return (constraints.width === currentMediaQuality.width.ideal &&
            constraints.height === currentMediaQuality.height.ideal);
    }
    getImgData() {
        const publisher = this.getPublisher();
        return new Promise((resolve) => {
            if (!publisher || !publisher.isLoading || !publisher.isLoading())
                return resolve(publisher);
            publisher.once('loaded', this.$timeout.bind(this.$timeout, resolve.bind(null, publisher), 500));
        }).then((publisher) => {
            if (!devices.selectors.hasCameraAccess(this.$ngRedux.getState()))
                return;
            const image = publisher && publisher.getImgData();
            const imageData = image && image.length > 10 && BASE64_PREFIX + image;
            if (imageData)
                this.imgDataCache = imageData;
            return imageData;
        });
    }
    getSessionId() {
        return this.OTSession.session.id;
    }
    // TODO https://doxyme.atlassian.net/browse/CS-6992
    async destroyPublishers() {
        const publisher = this.getPublisher();
        this.OTSession.publisher = undefined;
        super.removeLocalVideoElement();
        if (publisher) {
            await new Promise((resolve) => {
                publisher.destroy();
                this.on('destroyed', resolve);
                // Publisher does not provide any information about has it already been destroyed or not
                // If there is no destroy event during 2 seconds we resolve by timeout
                const MAX_WAITING_TIME = 2000;
                setTimeout(resolve, MAX_WAITING_TIME);
            });
        }
    }
    async togglePublishersVideo(condition) {
        const hasCameraAccess = devices.selectors.hasCameraAccess(this.$ngRedux.getState());
        if (typeof condition !== 'boolean') {
            condition = !localMedia.selectors.hasVideo(this.$ngRedux.getState());
        }
        if (hasCameraAccess) {
            this.OTSession.togglePublishersVideo(condition);
        }
        else {
            const hasCamera = devices.selectors.hasCamera(this.$ngRedux.getState());
            if (!hasCamera)
                return;
            await this.requestMediaAccess({ video: true });
            this.OTSession.togglePublishersVideo(condition);
        }
    }
    async togglePublishersAudio(condition) {
        const hasMicrophoneAccess = devices.selectors.hasMicrophoneAccess(this.$ngRedux.getState());
        if (typeof condition !== 'boolean') {
            condition = !localMedia.selectors.hasAudio(this.$ngRedux.getState());
        }
        if (hasMicrophoneAccess) {
            this.OTSession.togglePublishersAudio(condition);
        }
        else {
            const hasMicrophone = devices.selectors.hasMicrophone(this.$ngRedux.getState());
            if (!hasMicrophone)
                return;
            await this.requestMediaAccess({ audio: true });
            this.OTSession.togglePublishersAudio(condition);
        }
    }
    async requestMediaAccess(constraints) {
        const [err, stream] = await to(navigator.mediaDevices.getUserMedia(constraints));
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
        }
        if (err) {
            await this.$ngRedux.dispatch(devices.actions.updateDevices());
            this.startCameraAccessPoll();
            return;
        }
        await this.initPublisher();
        this.$ngRedux.dispatch(devices.actions.updateDevices());
    }
    toggleScreenSharing() {
        OT.registerScreenSharingExtension('chrome', 'hpohjmcciikpccgjfbjopmjcieefnaem', 2);
        return new Promise((resolve, reject) => {
            OT.checkScreenSharingCapability((response) => {
                if (response.supported && !response.extensionRequired)
                    return resolve(response);
                if (!response.supported || response.extensionRegistered === false) {
                    // This browser does not support screen sharing
                    this.screenshareFactory.notSupported();
                    reject(response);
                }
                else if (response.extensionInstalled === false) {
                    // Prompt to install the extension
                    this.screenshareFactory.pluginNotInstalled(response.extensionRequired);
                    reject(response);
                }
                else {
                    // Screen sharing is available.
                    resolve(response);
                }
            });
        });
    }
    async checkAudioStream() {
        if (this.isInCall)
            return;
        if (!this.ui.hasVideo)
            return;
        if (this.getUserMediaLastError)
            return;
        if (!this.OTSession.publisher)
            return;
        let max = 0;
        const checkAudioLevel = (event) => {
            max = event.audioLevel > max ? event.audioLevel : max;
        };
        this.OTSession.publisher.on('audioLevelUpdated', checkAudioLevel);
        await this.$timeout(() => {
            if (this.OTSession.publisher)
                this.OTSession.publisher.off('audioLevelUpdated', checkAudioLevel);
        }, 15000);
        if (max === 0)
            return Promise.reject();
    }
    waitForPublisher() {
        return new Promise((resolve, reject) => {
            this.OTSession.publisher.on(OTPublisherEvents.Destroyed, reject);
            const removeListener = this.$rootScope.$on(RootScopeEvents.OT_PUBLISHER_ERROR, (e, err) => {
                reject(err);
                removeListener();
            });
            if (this.OTSession.publisher) {
                if (!this.OTSession.publisher.isLoading())
                    return resolve();
                this.OTSession.publisher.once('loaded', this.$timeout.bind(this.$timeout, resolve, 500));
            }
            else {
                this.$rootScope.$on('otPublisherLoaded', () => resolve());
            }
        });
    }
    getSubscriberVideoElements() {
        return this.OTSession.subscribers.map((sub) => sub.videoElement());
    }
    getStreamIdByConnection(connectionId) {
        const stream = _.find(this.OTSession.streams, (stream) => {
            return stream.connection.id === connectionId;
        });
        if (stream)
            return stream.id;
    }
    normalizeError(error) {
        const OT_ERROR_MAP = {
            OT_HARDWARE_UNAVAILABLE: 'NotReadableError',
            OT_MEDIA_ERR_ABORTED: 'AbortError',
            OT_UNABLE_TO_CAPTURE_MEDIA: 'AbortError',
            OT_USER_MEDIA_ACCESS_DENIED: OTPublisherErrors.NotAllowedError,
            OT_NO_DEVICES_FOUND: 'NotFoundError',
        };
        if (!OT_ERROR_MAP[error.name])
            return error;
        const name = OT_ERROR_MAP[error.name];
        const translatedError = new Error(error.message);
        translatedError.name = name;
        translatedError.stack = error.stack;
        return translatedError;
    }
    async updateCameraSettings(cameraId) {
        const accessibleCameras = devices.selectors.getAccessibleCameras(this.$ngRedux.getState());
        this.updateIsRearCameraUsed();
        this.currentCamera = accessibleCameras.find((camera) => camera.deviceId === cameraId);
        const currentDevicesInfo = {
            cameraId: cameraId,
        };
        if (this.Browser.isMobile) {
            currentDevicesInfo.facingMode = this.isRearCameraUsed
                ? DeviceFacingMode.environment
                : DeviceFacingMode.user;
        }
        this.$ngRedux.dispatch(devices.actions.updateCurrentDevices(currentDevicesInfo));
    }
    setIsReconnecting(value) {
        this.debug('setIsReconnecting', value);
        this.isReconnecting = value;
    }
    isSelfConnection(connectionId) {
        return this.selfConnection.id === connectionId;
    }
    /**
     * Creates a hidden subscriber to receive self-captions
     * This is needed because publishers cannot receive their own caption events
     */
    createSelfCaptionsSubscriber() {
        this.destroySelfCaptionsSubscriber();
        const publisherStream = this.OTSession.publisher.stream;
        this.selfCaptionsSubscriber = this.OTSession.session.subscribe(publisherStream, null, {
            insertDefaultUI: false,
            subscribeToAudio: false,
            subscribeToVideo: false,
        }, (err) => {
            if (err != null) {
                this.$rootScope.$emit(RootScopeEvents.CLOSED_CAPTIONS.FAIL_SELF_CAPTION_CREATION, err);
                this.selfCaptionsSubscriber = null;
            }
        });
        this.selfCaptionsSubscriber.subscribeToCaptions(true).catch((error) => {
            this.$rootScope.$emit(RootScopeEvents.CLOSED_CAPTIONS.FAIL_SELF_CAPTION_SUBSCRIPTION, error);
            throw new Error(error);
        });
        // Handle caption received events for self-captions
        this.selfCaptionsSubscriber.on('captionReceived', (captionEvent) => {
            const currentState = this.$ngRedux.getState();
            const currentCaptions = getCaptions(currentState);
            const selfParticipant = call.selectors.getSelfParticipant(currentState);
            const newCaption = {
                name: localeService.formatMessageById('video-chat.actions.preview'),
                streamId: selfParticipant.uuid,
                caption: captionEvent.caption,
                isFinal: Boolean(captionEvent.isFinal),
                showName: true,
            };
            const updatedCaptions = updateCaptionsArray(currentCaptions, newCaption);
            this.$ngRedux.dispatch(setCaptions(updatedCaptions));
        });
    }
    /**
     * Destroys the hidden self-captions subscriber
     */
    destroySelfCaptionsSubscriber() {
        if (this.selfCaptionsSubscriber !== null) {
            try {
                this.OTSession.session.unsubscribe(this.selfCaptionsSubscriber);
                this.$rootScope.$emit(RootScopeEvents.CLOSED_CAPTIONS.SELF_CAPTION_DESTROYED);
            }
            catch (error) {
                console.warn('[Closed Captions]: Error destroying self-captions subscriber:', error);
            }
            finally {
                this.selfCaptionsSubscriber = null;
            }
        }
    }
}
